import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Plus, FileText, Calendar, User,
  Edit, Trash2, Tag, Filter,
  ThumbsUp, AlertCircle, Package, Layers,
  ArrowUpDown, Clock, TrendingUp,
  CheckCircle, Archive, X, AlertTriangle
} from 'lucide-react';
import { 
  Input, Button, Card, Badge, Spin, Empty, 
  Select, Space, Typography, message, Row, Col, Modal
} from 'antd';
import api from '../../utils/api';
import './KnowledgeBase.css';

const { Title, Text } = Typography;
const { Option } = Select;

const KnowledgeBaseHome = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    type: 'all',
    status: 'all',
    product: 'all',
    productCategory: 'all',
    sortBy: 'newest'
  });
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    types: [],
    products: [],
    productCategories: [],
    statuses: ['draft', 'published', 'archived']
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });
  
  // DELETE MODAL STATES
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [confirmationError, setConfirmationError] = useState(false);
  
  const navigate = useNavigate();

  const statusOptions = [
    { value: 'all', label: 'All Statuses', icon: <Filter size={14} /> },
    { value: 'published', label: 'Published', icon: <CheckCircle size={14} />, color: '#38a169' },
    { value: 'draft', label: 'Draft', icon: <FileText size={14} />, color: '#ed8936' },
    { value: 'archived', label: 'Archived', icon: <Archive size={14} />, color: '#718096' }
  ];

  // Fetch articles
  const fetchArticles = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: filters.sortBy
      };

      if (searchTerm) params.search = searchTerm;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.product !== 'all') params.product = filters.product;
      if (filters.productCategory !== 'all') params.productCategory = filters.productCategory;

      const response = await api.get('/api/kb-articles', { params });
      
      if (response.data.success) {
        setArticles(response.data.data);
        setPagination(response.data.pagination || {
          total: response.data.data.length,
          page: 1,
          limit: 20,
          totalPages: 1
        });
      }
    } catch (error) {
      console.error('❌ Error fetching articles:', error);
      message.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      
      const response = await api.get('/api/kb-articles/options/filters');
      
      if (response.data.success) {
        setFilterOptions({
          categories: response.data.data.categories || [],
          types: response.data.data.types || [],
          products: response.data.data.products || [],
          productCategories: response.data.data.productCategories || [],
          statuses: ['draft', 'published', 'archived']
        });
      } else {
        message.warning('Could not load filter options');
      }
    } catch (error) {
      console.error('❌ Error fetching filter options:', error);
      message.error('Failed to load filter options');
    } finally {
      setLoadingFilters(false);
    }
  };

  // Fetch articles when filters or pagination changes
  useEffect(() => {
    fetchArticles();
  }, [searchTerm, filters, pagination.page]);

  // Fetch filter options only once on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Show delete confirmation modal
  const showDeleteModal = (article) => {
    const articleId = getArticleId(article);
    if (!articleId) {
      message.error('Cannot delete: Article ID not found');
      return;
    }
    
    // Reset confirmation states when opening modal
    setConfirmationText('');
    setConfirmationError(false);
    
    setArticleToDelete({
      id: articleId,
      title: article.title || 'Untitled Article',
      category: article.categoryName || 'No Category',
      type: article.typeName || 'No Type',
      author: article.authorName || 'Unknown Author',
      createdAt: article.createdAt
    });
    setDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;
    
    // Validate confirmation text
    if (confirmationText.trim().toUpperCase() !== 'DELETE') {
      setConfirmationError(true);
      message.error('Please type "DELETE" exactly as shown to confirm deletion.');
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      await api.delete(`/api/kb-articles/${articleToDelete.id}`);
      
      message.success(`"${articleToDelete.title}" deleted successfully`);
      
      // Refresh articles and filter options
      fetchArticles();
      fetchFilterOptions();
      
      // Close modal and reset states
      setDeleteModalVisible(false);
      setArticleToDelete(null);
      setConfirmationText('');
      setConfirmationError(false);
      
    } catch (error) {
      console.error('Error deleting article:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 404) {
          message.error('Article not found. It may have already been deleted.');
        } else if (status === 409) {
          message.error('Cannot delete article because it is referenced by other records.');
        } else {
          message.error(data.message || 'Failed to delete article');
        }
      } else {
        message.error('Failed to delete article. Please try again.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setArticleToDelete(null);
    setDeleteLoading(false);
    setConfirmationText('');
    setConfirmationError(false);
  };

  // Handle confirmation text change
  const handleConfirmationChange = (e) => {
    const value = e.target.value;
    setConfirmationText(value);
    
    // Clear error when user starts typing
    if (confirmationError && value.trim().toUpperCase() === 'DELETE') {
      setConfirmationError(false);
    }
  };

  // Check if delete button should be disabled
  const isDeleteDisabled = () => {
    return confirmationText.trim().toUpperCase() !== 'DELETE';
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Helper function to get priority info
  const getPriorityInfo = (priorityId) => {
    const priorities = {
      1: { label: 'High', color: '#f56565', bgColor: '#fed7d7' },
      2: { label: 'Medium', color: '#ed8936', bgColor: '#feebc8' },
      3: { label: 'Low', color: '#38a169', bgColor: '#c6f6d5' }
    };
    return priorities[priorityId] || { label: 'Unknown', color: '#718096', bgColor: '#e2e8f0' };
  };

  // Helper function to get status info
  const getStatusInfo = (status) => {
    const statuses = {
      published: { label: 'Published', color: '#38a169', bgColor: '#c6f6d5' },
      draft: { label: 'Draft', color: '#ed8936', bgColor: '#feebc8' },
      archived: { label: 'Archived', color: '#718096', bgColor: '#e2e8f0' }
    };
    return statuses[status] || { label: 'Unknown', color: '#718096', bgColor: '#e2e8f0' };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format date for modal (more detailed)
  const formatModalDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get popular tags (most frequently used)
  const getPopularTags = () => {
    const tagCounts = {};
    
    articles.forEach(article => {
      if (article.tags && Array.isArray(article.tags)) {
        article.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });
    
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([tag]) => tag);
  };

  // Get all unique products from displayed articles
  const getAllProducts = () => {
    return [...new Set(articles.map(a => a.productName).filter(Boolean))];
  };

  // Get article ID
  const getArticleId = (article) => {
    // Try all possible ID field names
    return article.id || article._id || article.articleId || 
           article.uuid || article.articleUuid || 
           article.article_id || null;
  };

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchArticles();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      type: 'all',
      status: 'all',
      product: 'all',
      productCategory: 'all',
      sortBy: 'newest'
    });
    setSearchTerm('');
    message.info('All filters cleared');
  };

  return (
    <div className="kb-page">
      {/* Header */}
      <div className="kb-header">
        <Title level={1} className="kb-title">Knowledge Base</Title>
        <Text className="kb-description">
          Comprehensive documentation, guides, and solutions for common issues
        </Text>
      </div>

      <div className="kb-main-card">
        <div className="kb-content">
          {/* Toolbar */}
          <div className="kb-toolbar">
            <div>
              <Title level={4} style={{ margin: 0 }}>Knowledge Base Articles</Title>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {pagination.total} articles
              </Text>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                type="primary"
                icon={<Plus size={16} />}
                onClick={() => navigate('/knowledge-base/new')}
                className="kb-create-button"
                size="large"
              >
                Create Article
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <Card className="kb-filter-section">
            <Row className="filter-container">
              {/* First Row: Full width search */}
              <Col span={24}>
                <div className="kb-search-box">
                  <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                    <Search size={14} style={{ marginRight: '8px' }} />
                    Search Articles
                  </Text>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Input
                      placeholder="Search by title, content, keywords, or tags..."
                      prefix={<Search size={16} style={{ color: '#a0aec0' }} />}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="kb-search-input"
                      size="large"
                      allowClear
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="link"
                      onClick={handleClearFilters}
                      disabled={filters.category === 'all' && filters.type === 'all' && filters.status === 'all' && filters.product === 'all' && filters.productCategory === 'all' && !searchTerm}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </Col>
              
              {/* Second Row: All filters in one row */}
              <Col span={24}>
                <Row className="filter-row-two" gutter={[16, 16]}>
                  {/* Ticket Category Filter */}
                  <Col xs={24} sm={12} md={8} lg={4}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                        Ticket Category
                      </Text>
                      <Select
                        value={filters.category}
                        onChange={(value) => handleFilterChange('category', value)}
                        className="kb-filter-select"
                        size="large"
                        style={{ width: '100%' }}
                        loading={loadingFilters}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        <Option value="all">All Ticket Categories</Option>
                        {filterOptions.categories.map((category, index) => (
                          <Option key={category || `cat-${index}`} value={category}>
                            {category}
                          </Option>
                        ))}
                        {filterOptions.categories.length === 0 && !loadingFilters && (
                          <Option value="none" disabled>No categories available</Option>
                        )}
                      </Select>
                    </div>
                  </Col>
                  
                  {/* Article Type Filter */}
                  <Col xs={24} sm={12} md={8} lg={4}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                        Article Type
                      </Text>
                      <Select
                        value={filters.type}
                        onChange={(value) => handleFilterChange('type', value)}
                        className="kb-filter-select"
                        size="large"
                        style={{ width: '100%' }}
                        loading={loadingFilters}
                        showSearch
                      >
                        <Option value="all">All Types</Option>
                        {filterOptions.types.map((type, index) => (
                          <Option key={type || `type-${index}`} value={type}>
                            {type}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                  
                  {/* Article Status Filter */}
                  <Col xs={24} sm={12} md={8} lg={4}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                        Article Status
                      </Text>
                      <Select
                        value={filters.status}
                        onChange={(value) => handleFilterChange('status', value)}
                        className="kb-filter-select"
                        size="large"
                        style={{ width: '100%' }}
                      >
                        {statusOptions.map(status => (
                          <Option key={status.value} value={status.value}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {status.icon}
                              <span style={{ 
                                color: status.color || 'inherit',
                                fontWeight: status.value !== 'all' ? '500' : 'normal'
                              }}>
                                {status.label}
                              </span>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                  
                  {/* Product Filter */}
                  <Col xs={24} sm={12} md={8} lg={4}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                        Product
                      </Text>
                      <Select
                        value={filters.product}
                        onChange={(value) => handleFilterChange('product', value)}
                        className="kb-filter-select"
                        size="large"
                        style={{ width: '100%' }}
                        loading={loadingFilters}
                        showSearch
                      >
                        <Option value="all">All Products</Option>
                        {filterOptions.products.map((product, index) => (
                          <Option key={product || `prod-${index}`} value={product}>
                            {product}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Col>

                  {/* Product Category Filter */}
                  <Col xs={24} sm={12} md={8} lg={4}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                        Product Category
                      </Text>
                      <Select
                        value={filters.productCategory}
                        onChange={(value) => handleFilterChange('productCategory', value)}
                        className="kb-filter-select"
                        size="large"
                        style={{ width: '100%' }}
                        loading={loadingFilters}
                        showSearch
                      >
                        <Option value="all">All Product Categories</Option>
                        {filterOptions.productCategories.map((category, index) => (
                          <Option key={category || `pc-${index}`} value={category}>
                            {category}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Col>

                  {/* Sort By Filter */}
                  <Col xs={24} sm={12} md={8} lg={4}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#4a5568' }}>
                        <ArrowUpDown size={14} style={{ marginRight: '8px' }} />
                        Sort By
                      </Text>
                      <Select
                        value={filters.sortBy}
                        onChange={(value) => handleFilterChange('sortBy', value)}
                        className="kb-filter-select"
                        size="large"
                        style={{ width: '100%' }}
                      >
                        <Option value="newest">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={14} /> Newest First
                          </div>
                        </Option>
                        <Option value="oldest">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={14} /> Oldest First
                          </div>
                        </Option>
                        <Option value="helpful">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ThumbsUp size={14} /> Most Helpful
                          </div>
                        </Option>
                        <Option value="title">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FileText size={14} /> Title A-Z
                          </div>
                        </Option>
                      </Select>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>

          {/* Filter Status */}
          {(!loadingFilters && filterOptions.categories.length === 0 && filterOptions.products.length === 0) && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '8px', 
              marginBottom: '24px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <AlertCircle size={20} color="#0ea5e9" />
                <div>
                  <Text strong style={{ color: '#0369a1' }}>No filter options available</Text>
                  <Text style={{ display: 'block', color: '#0c4a6e', fontSize: '14px' }}>
                    Create some articles with categories and products first.
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="kb-stat-card">
                <div className="stat-content">
                  <FileText size={24} className="stat-icon" style={{ color: '#4299e1' }} />
                  <div>
                    <div className="stat-number">{pagination.total}</div>
                    <div className="stat-label">Articles</div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="kb-stat-card">
                <div className="stat-content">
                  <ThumbsUp size={24} className="stat-icon" style={{ color: '#38a169' }} />
                  <div>
                    <div className="stat-number">
                      {articles.reduce((sum, article) => sum + (article.helpfulCount || 0), 0).toLocaleString()}
                    </div>
                    <div className="stat-label">Helpful Votes</div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="kb-stat-card">
                <div className="stat-content">
                  <Package size={24} className="stat-icon" style={{ color: '#9f7aea' }} />
                  <div>
                    <div className="stat-number">{getAllProducts().length}</div>
                    <div className="stat-label">Products</div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="kb-stat-card">
                <div className="stat-content">
                  <Tag size={24} className="stat-icon" style={{ color: '#ed8936' }} />
                  <div>
                    <div className="stat-number">
                      {articles.reduce((sum, article) => sum + (article.tags?.length || 0), 0)}
                    </div>
                    <div className="stat-label">Total Tags</div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Articles List */}
          {loading ? (
            <div className="kb-loading-container">
              <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#718096' }}>Loading articles...</div>
            </div>
          ) : articles.length === 0 ? (
            <Empty
              className="kb-empty-state"
              description={
                <div>
                  <div style={{ fontSize: '18px', color: '#2d3748', marginBottom: '8px' }}>
                    No articles found
                  </div>
                  <div style={{ fontSize: '14px', color: '#718096' }}>
                    {searchTerm ? `No results for "${searchTerm}"` : 'Create your first knowledge base article'}
                  </div>
                </div>
              }
              imageStyle={{ height: 120 }}
            >
              <Button 
                type="primary" 
                onClick={() => navigate('/knowledge-base/new')}
                className="kb-create-button"
                size="large"
              >
                Create First Article
              </Button>
            </Empty>
          ) : (
            <>
              {/* Active Filters Summary */}
              {(filters.category !== 'all' || filters.type !== 'all' || filters.status !== 'all' || 
                filters.product !== 'all' || filters.productCategory !== 'all' || searchTerm) && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px 16px', 
                  backgroundColor: '#f7fafc', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <Text strong style={{ color: '#4a5568', fontSize: '14px' }}>Active Filters:</Text>
                    {filters.category !== 'all' && (
                      <Badge count={filters.category} style={{ backgroundColor: '#4299e1', marginRight: '4px' }} />
                    )}
                    {filters.type !== 'all' && (
                      <Badge count={filters.type} style={{ backgroundColor: '#ed8936', marginRight: '4px' }} />
                    )}
                    {filters.status !== 'all' && (
                      <Badge count={filters.status} style={{ backgroundColor: '#38a169', marginRight: '4px' }} />
                    )}
                    {filters.product !== 'all' && (
                      <Badge count={filters.product} style={{ backgroundColor: '#9f7aea', marginRight: '4px' }} />
                    )}
                    {filters.productCategory !== 'all' && (
                      <Badge count={filters.productCategory} style={{ backgroundColor: '#f56565', marginRight: '4px' }} />
                    )}
                    {searchTerm && (
                      <Badge count={`Search: ${searchTerm}`} style={{ backgroundColor: '#718096', marginRight: '4px' }} />
                    )}
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={handleClearFilters}
                      style={{ 
                        color: '#4299e1', 
                        padding: '0 8px', 
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="kb-articles-grid">
                {articles.map(article => {
                  const articleId = getArticleId(article);
                  const priorityInfo = getPriorityInfo(article.priorityId);
                  const statusInfo = getStatusInfo(article.status);
                  
                  return (
                    <Card key={articleId} className="kb-article-card" hoverable>
                      <div className="kb-article-card-content">
                        <div className="kb-article-header">
                          <div className="kb-article-meta-left">
                            {article.categoryName && (
                              <Badge 
                                className="kb-category-badge"
                                style={{ 
                                  backgroundColor: '#e2e8f0',
                                  color: '#4a5568'
                                }}
                              >
                                {article.categoryName}
                              </Badge>
                            )}
                            {article.typeName && (
                              <span className="kb-article-type">
                                <Layers size={10} style={{ marginRight: '4px' }} />
                                {article.typeName}
                              </span>
                            )}
                          </div>
                          <div className="kb-article-status">
                            <span 
                              className="kb-status-badge"
                              style={{
                                backgroundColor: statusInfo.bgColor,
                                color: statusInfo.color,
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              {statusInfo.label}
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="kb-article-title">
                          <Link to={`/knowledge-base/${articleId}`} onClick={(e) => {
                            e.preventDefault();
                            navigate(`/knowledge-base/${articleId}`);
                          }}>
                            {article.title}
                          </Link>
                        </h3>
                        
                        <p className="kb-article-excerpt">{article.excerpt || 'No description available'}</p>
                        
                        {article.productName && (
                          <div className="kb-article-product">
                            <Package size={12} style={{ marginRight: '4px', color: '#718096' }} />
                            <span style={{ fontSize: '12px', color: '#4a5568' }}>
                              {article.productName}
                            </span>
                            {article.productCategoryName && (
                              <span style={{ fontSize: '11px', color: '#a0aec0', marginLeft: '8px' }}>
                                ({article.productCategoryName})
                              </span>
                            )}
                          </div>
                        )}
                        
                        {article.tags && article.tags.length > 0 && (
                          <div className="kb-article-tags">
                            {article.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="kb-article-tag" onClick={() => setSearchTerm(tag)} style={{ cursor: 'pointer' }}>
                                #{tag}
                              </span>
                            ))}
                            {article.tags.length > 3 && (
                              <span className="kb-article-tag-more">+{article.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                        
                        <div className="kb-article-footer">
                          <div className="kb-article-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                              {article.authorName && (
                                <span className="kb-meta-item">
                                  <User size={12} /> {article.authorName}
                                </span>
                              )}
                              <span className="kb-meta-item">
                                <Calendar size={12} /> {formatDate(article.createdAt)}
                              </span>
                              <span className="kb-meta-item">
                                <ThumbsUp size={12} /> {article.helpfulCount || 0}
                              </span>
                            </div>
                            
                            {article.priorityId && (
                              <div style={{ marginTop: '8px' }}>
                                <span 
                                  className="kb-priority-badge"
                                  style={{
                                    backgroundColor: priorityInfo.bgColor,
                                    color: priorityInfo.color,
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '500'
                                  }}
                                >
                                  {priorityInfo.label} Priority
                                </span>
                              </div>
                            )}
                          </div>
                                                  
                          <div className="kb-article-actions">
                            <Space size="small">
                              <Button
                                type="text"
                                icon={<Edit size={14} />}
                                onClick={() => navigate(`/knowledge-base/edit/${articleId}`)}
                                className="kb-action-button kb-edit-button"
                                size="small"
                                title="Edit Article"
                              />
                              <Button
                                type="text"
                                danger
                                icon={<Trash2 size={14} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  showDeleteModal(article);
                                }}
                                className="kb-action-button kb-delete-button"
                                size="small"
                                title="Delete Article"
                              />
                            </Space>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {/* Pagination */}
          {!loading && articles.length > 0 && pagination.totalPages > 1 && (
            <div className="kb-pagination">
              <Space>
                <Button
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </Button>
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </Button>
              </Space>
            </div>
          )}

          {/* Popular Tags Section */}
          {!loading && articles.length > 0 && getPopularTags().length > 0 && (
            <Card className="kb-form-section" style={{ marginTop: '32px' }}>
              <div className="kb-section-title">
                <Tag size={18} />
                Popular Tags
              </div>
              <div className="kb-tags">
                {getPopularTags().map(tag => (
                  <span 
                    key={tag} 
                    className="kb-tag" 
                    onClick={() => setSearchTerm(tag)} 
                    style={{ cursor: 'pointer' }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* ENHANCED DELETE CONFIRMATION MODAL */}
      <Modal
        open={deleteModalVisible}
        onCancel={handleDeleteCancel}
        footer={null}
        centered
        width={480}
        closable={false}
        style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif" }}
        bodyStyle={{ padding: 0, borderRadius: '16px', overflow: 'hidden' }}
      >
        {/* Modal Header */}
        <div style={{
          padding: '24px 24px 20px 24px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AlertTriangle size={20} color="#dc2626" />
            </div>
            <div>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937',
                fontFamily: "'Inter', sans-serif" 
              }}>
                Delete Article
              </h3>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px', 
                color: '#6b7280',
                fontFamily: "'Inter', sans-serif" 
              }}>
                This action cannot be undone
              </p>
            </div>
          </div>
          <Button
            type="text"
            icon={<X size={18} />}
            onClick={handleDeleteCancel}
            style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '8px',
              color: '#9ca3af'
            }}
          />
        </div>

        {/* Modal Content */}
        <div style={{ padding: '24px' }}>
          {/* Warning Message */}
          <div style={{
            padding: '16px',
            backgroundColor: '#fef2f2',
            borderRadius: '10px',
            border: '1px solid #fecaca',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <AlertTriangle size={18} color="#dc2626" />
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#991b1b',
                  fontFamily: "'Inter', sans-serif" 
                }}>
                  Warning: Permanent Deletion
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: '#7f1d1d',
                  lineHeight: '1.5',
                  fontFamily: "'Inter', sans-serif" 
                }}>
                  All article data including content, metadata, and feedback will be permanently deleted.
                </p>
              </div>
            </div>
          </div>

          {/* Article Details */}
          {articleToDelete && (
            <div style={{ 
              backgroundColor: '#f9fafb', 
              padding: '18px', 
              borderRadius: '12px', 
              marginBottom: '24px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                <FileText size={18} color="#4b5563" />
                <div style={{ flex: 1 }}>
                  <h4 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#111827',
                    fontFamily: "'Inter', sans-serif" 
                  }}>
                    {articleToDelete.title}
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', rowGap: '8px' }}>
                    <div>
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#6b7280', 
                        fontWeight: '500',
                        fontFamily: "'Inter', sans-serif" 
                      }}>
                        ARTICLE ID
                      </span>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '13px', 
                        color: '#374151', 
                        fontWeight: '500',
                        fontFamily: "'Inter', sans-serif" 
                      }}>
                        {articleToDelete.id}
                      </p>
                    </div>
                    <div>
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#6b7280', 
                        fontWeight: '500',
                        fontFamily: "'Inter', sans-serif" 
                      }}>
                        CATEGORY
                      </span>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '13px', 
                        color: '#374151', 
                        fontWeight: '500',
                        fontFamily: "'Inter', sans-serif" 
                      }}>
                        {articleToDelete.category}
                      </p>
                    </div>
                    <div>
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#6b7280', 
                        fontWeight: '500',
                        fontFamily: "'Inter', sans-serif" 
                      }}>
                        AUTHOR
                      </span>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '13px', 
                        color: '#374151', 
                        fontWeight: '500',
                        fontFamily: "'Inter', sans-serif" 
                      }}>
                        {articleToDelete.author}
                      </p>
                    </div>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#6b7280', 
                      fontWeight: '500',
                      fontFamily: "'Inter', sans-serif" 
                    }}>
                      CREATED
                    </span>
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '13px', 
                      color: '#374151', 
                      fontWeight: '500',
                      fontFamily: "'Inter', sans-serif" 
                    }}>
                      {formatModalDate(articleToDelete.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Input */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '13px', 
              fontWeight: '500', 
              color: '#374151',
              fontFamily: "'Inter', sans-serif" 
            }}>
              Type "DELETE" to confirm
            </label>
            <Input
              id="delete-confirmation"
              placeholder='Type "DELETE" here...'
              value={confirmationText}
              onChange={handleConfirmationChange}
              status={confirmationError ? 'error' : ''}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                padding: '10px 14px',
                borderRadius: '8px',
                borderColor: confirmationError ? '#f87171' : undefined
              }}
              onPressEnter={(e) => {
                if (!isDeleteDisabled()) {
                  handleDeleteConfirm();
                }
              }}
            />
            {confirmationError && (
              <div style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <AlertTriangle size={12} />
                <span>Please type "DELETE" exactly as shown to confirm deletion.</span>
              </div>
            )}
            <div style={{ 
              marginTop: '8px', 
              fontSize: '12px', 
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              <span>Case-insensitive. Current input: </span>
              <code style={{ 
                backgroundColor: '#f3f4f6', 
                padding: '2px 6px', 
                borderRadius: '4px',
                fontFamily: 'monospace'
              }}>
                {confirmationText || '(empty)'}
              </code>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: '500',
              fontSize: '14px',
              padding: '10px 20px',
              height: 'auto',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              color: '#4b5563'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleDeleteConfirm}
            loading={deleteLoading}
            disabled={isDeleteDisabled()}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: '600',
              fontSize: '14px',
              padding: '10px 24px',
              height: 'auto',
              borderRadius: '8px',
              background: isDeleteDisabled() 
                ? '#f3f4f6' 
                : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              color: isDeleteDisabled() ? '#9ca3af' : 'white',
              border: 'none',
              boxShadow: isDeleteDisabled() ? 'none' : '0 2px 6px rgba(220, 38, 38, 0.25)',
              cursor: isDeleteDisabled() ? 'not-allowed' : 'pointer'
            }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Article'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeBaseHome;